import { APP_BASE_HREF } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, inject } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { QUOTE_TOOL_STORAGE_KEY } from '@lx/quote-tool/lite/shared/types';
import { TimezoneService } from '@lx/quote-tool/shared/services';
import { getBaseHrefFactory, getPaymentsGatewayVariables, setBaseHrefFactory } from '@lx/quote-tool/shared/utils/functions';
import { ensureStringOrUndefined } from '@lx/shared/utils';
import { configFactory, CustomisedNGXLoggerServerService, localeFactory, registerLocale } from '@lx/shared/app-init';
import { httpErrorInterceptorFn, WHITELISTED_ERROR_CODES } from '@lx/shared/errors';
import { PAYMENTS_GATEWAY_ENVIRONMENT_VARIABLES } from '@lx/shared/payment';
import { APP_NAME, BASE_HREF, ENVIRONMENT, getCompanyHeader } from '@lx/shared/tokens';
import { LoggerModule, NGXLogger, NgxLoggerLevel, TOKEN_LOGGER_SERVER_SERVICE } from 'ngx-logger';
import { appRoutes } from './app.routes';
import { STORAGE_KEY } from './types';
import {
  getAppInitializers,
  getCompanyConfigApplicationProvider,
  getDataLayerProviders,
  getHostDataReaderApplicationProvider,
  getStoreOrchestratorApplicationProvider,
  getUpdatedQuoteTrackingService,
  getViewPageTrackingService,
} from './utils';
import { APPLICATION_CONFIGURATION_SERVICE } from '@lx/shared/app-config';
import { ApplicationConfigurationService } from './application-configuration.service';
import { getCoreProviders } from '@lx/shared/app-init/functions/get-core-providers.function';
import { getErrorHandlerProviders } from '@lx/shared/app-init/functions/get-error-handler-providers.function';
import { PrimeNGConfig } from 'primeng/api';
import { loggingFactory } from '@lx/shared/app-init/functions/logging.factory';
import { QtLiteEnvironment } from './types';
import { GtmInitializationService, ReturnUrlService } from '@lx/quote-tool/lite/shared/services';

export const getAppConfig = (runtimeEnvironment: QtLiteEnvironment): ApplicationConfig => ({
  providers: [
    provideAnimations(),
    provideHttpClient(withInterceptors([httpErrorInterceptorFn])),
    // Set up BASE_HREF and APP_BASE_HREF BEFORE router
    { provide: BASE_HREF, useFactory: getBaseHrefFactory },
    { provide: APP_BASE_HREF, useFactory: setBaseHrefFactory, deps: [BASE_HREF] },
    // Now provide router so it uses the correct base href
    provideRouter(appRoutes),
    importProvidersFrom(
      LoggerModule.forRoot(
        {
          level: NgxLoggerLevel.DEBUG,
          disableConsoleLogging: false,
          serverLogLevel: NgxLoggerLevel.ERROR,
        },
        {
          serverProvider: {
            provide: TOKEN_LOGGER_SERVER_SERVICE,
            useClass: CustomisedNGXLoggerServerService,
          },
        },
      ),
    ),
    { provide: APPLICATION_CONFIGURATION_SERVICE, useClass: ApplicationConfigurationService },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const configService = inject(APPLICATION_CONFIGURATION_SERVICE);
        const gtmService = inject(GtmInitializationService);
        const returnUrlService = inject(ReturnUrlService);

        return async () => {
          // GTM and returnUrl services must be initialized immediately after configuration is loaded to ensure
          // the container ID is available, the GTM script is injected before page views are tracked,
          // and the return URL is properly configured for navigation.
          const configData = await configFactory(runtimeEnvironment, configService)();

          const gtmContainerId = ensureStringOrUndefined(configData.QUOTE_TOOL_GTM_CONTAINER_ID);
          gtmService.initialize(gtmContainerId);

          const returnUrl = ensureStringOrUndefined(configData.QUOTE_TOOL_RETURN_URL);
          returnUrlService.initialize(returnUrl);
        };
      },
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        registerLocale(runtimeEnvironment.LOCALE);
        const primeNGConfig = inject(PrimeNGConfig);
        return localeFactory(primeNGConfig);
      },
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const configService = inject(APPLICATION_CONFIGURATION_SERVICE);
        const loggingService = inject(NGXLogger);
        const companyHeader = getCompanyHeader();
        return loggingFactory(configService, loggingService, companyHeader, runtimeEnvironment);
      },
      multi: true,
    },
    ...getCoreProviders(runtimeEnvironment),
    ...getErrorHandlerProviders(),
    ...getCompanyConfigApplicationProvider(runtimeEnvironment.COMPANY),
    ...getStoreOrchestratorApplicationProvider(),
    ...getHostDataReaderApplicationProvider(),
    ...getAppInitializers(),
    ...getViewPageTrackingService(),
    ...getUpdatedQuoteTrackingService(),
    ...getDataLayerProviders(),
    { provide: PAYMENTS_GATEWAY_ENVIRONMENT_VARIABLES, deps: [ENVIRONMENT], useFactory: getPaymentsGatewayVariables },
    { provide: WHITELISTED_ERROR_CODES, useValue: [410, 404] },
    { provide: APP_NAME, useValue: 'quote-tool-lite' },
    { provide: QUOTE_TOOL_STORAGE_KEY, useValue: STORAGE_KEY },
    TimezoneService,
  ],
});
